// src/DoctorFeedback.js
import React, { useState, useEffect, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { AuthContext } from '../context/AuthContext';
import api from "../utils/api";
import DoctorSidebar from "./DoctorSidebar";
import "./DoctorFeedback.css";

const DoctorFeedback = () => {
  const { doctorUser } = useContext(AuthContext);
  const navigate = useNavigate();
  const [feedbacks, setFeedbacks] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(window.innerWidth > 768); // Responsive sidebar

  useEffect(() => {
    const fetchFeedback = async () => {
      if (!doctorUser || doctorUser.role !== "doctor") {
        setError("You must be logged in as a doctor to view feedback.");
        setTimeout(() => navigate('/doctor-login'), 2000);
        setIsLoading(false);
        return;
      }

      try {
        const response = await api.get(`/feedback/doctor/${doctorUser.id}`, {
          headers: { Authorization: `Bearer ${doctorUser.accessToken}` },
        });
        setFeedbacks(response.data || []);
        setError(null);
      } catch (err) {
        const errorMessage = err.response?.data?.message || err.message || "Network error";
        setError(`Failed to load feedback: ${errorMessage}`);
        setTimeout(() => {
          api.get(`/feedback/doctor/${doctorUser.id}`, {
            headers: { Authorization: `Bearer ${doctorUser.accessToken}` },
          })
            .then((retryResponse) => {
              setFeedbacks(retryResponse.data || []);
              setError(null);
            })
            .catch(() => setError("Failed to load feedback after retry."));
        }, 2000);
      } finally {
        setIsLoading(false);
      }
    };
    fetchFeedback();
  }, [doctorUser, navigate]);

  useEffect(() => {
    const handleResize = () => {
      setSidebarOpen(window.innerWidth > 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const toggleSidebar = () => {
    setSidebarOpen((prev) => !prev);
  };

  if (isLoading) return (
    <div className="doctor-feedback-page">
      <DoctorSidebar isOpen={sidebarOpen} toggleSidebar={toggleSidebar} />
      <main className="feedback-main">
        <button className="sidebar-toggle" onClick={toggleSidebar}>
          {sidebarOpen ? "✕" : "☰"}
        </button>
        <div className="loading-overlay">
          <div className="loading-spinner"></div>
          <p>Loading your TureMedicine feedback...</p>
        </div>
      </main>
    </div>
  );

  if (error) return (
    <div className="doctor-feedback-page">
      <DoctorSidebar isOpen={sidebarOpen} toggleSidebar={toggleSidebar} />
      <main className="feedback-main">
        <button className="sidebar-toggle" onClick={toggleSidebar}>
          {sidebarOpen ? "✕" : "☰"}
        </button>
        <div className="error-message">
          <h1>Error</h1>
          <p>{error}</p>
          <button onClick={() => navigate("/doctor-login")}>Go to Login</button>
        </div>
      </main>
    </div>
  );

  return (
    <div className="doctor-feedback-page">
      <DoctorSidebar isOpen={sidebarOpen} toggleSidebar={toggleSidebar} />
      <main className="feedback-main">
        <button className="sidebar-toggle" onClick={toggleSidebar}>
          {sidebarOpen ? "✕" : "☰"}
        </button>
        <h1>Your Feedback & Ratings</h1>
        {feedbacks.length > 0 ? (
          <div className="feedback-list">
            {feedbacks.map((fb) => (
              <div key={fb._id} className="feedback-card">
                <p><strong>Patient:</strong> {fb.patientId?.name || "Anonymous"}</p>
                <p><strong>Rating:</strong> {fb.rating} / 5 ★</p>
                <p><strong>Feedback:</strong> {fb.feedback || "No comments"}</p>
                <p><strong>Date:</strong> {new Date(fb.createdAt).toLocaleString()}</p>
              </div>
            ))}
          </div>
        ) : (
          <p className="no-feedback">No feedback available yet.</p>
        )}
      </main>
    </div>
  );
};

export default DoctorFeedback;